import('jquery');

import filter from 'lodash/filter';
import find from 'lodash/find';
import map from 'lodash/map';
import remove from 'lodash/remove';

import { v4 as uuid4 } from 'uuid';

import React from 'react/cjs/react.production.min';
import ReactDOM from 'react-dom/cjs/react-dom.production.min';

import { CurrencySelectorComponent } from '../../_components/currency-selector.component.jsx';
import { TermSelectorComponent } from '../../_components/term-selector.component.jsx';
import { FeatureMatrixPriceComponent } from '../../_components/feature-matrix-price.component.jsx';
import { SolutionPackageComponent } from '../../_components/solution-package.component.jsx';

const COMPONENTS = {
  'CurrencySelectorComponent': CurrencySelectorComponent,
  'TermSelectorComponent': TermSelectorComponent,
  'FeatureMatrixPriceComponent': FeatureMatrixPriceComponent,
  'SolutionPackageComponent': SolutionPackageComponent,
};

const STORAGE_KEYS = {
  CURRENCY: 'qs-pref-currency',
  TERM: 'qs-pref-pricing-term',
  ESTIMATOR_STATE: 'qs-config-state-v4',
  PRICEBOOK_PREFIX: 'qs-config-pricebook-v4-',
  SOLUTIONS_PREFIX: 'qs-config-solutions-v4-',
  BUNDLES: 'qs-config-bundles-v4',
};

const CURRENCY_MAPPING = {
  SGD: ['SG'],
  GBP: ['GB', 'UK'],
  EUR: ['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES'],
};

const CURRENCY_SYMBOLS = {
  SGD: 'S$',
  GBP: '£',
  EUR: '€',
  USD: '$',
};

const DEFAULT_DISCOUNT_SCHEDULE = [{
  "term": 1,
  "percentage": 0
}, {
  "term": 12,
  "percentage": 0.15
}, {
  "term": 24,
  "percentage": 0.20
}, {
  "term": 36,
  "percentage": 0.25
}];

const ESTIMATOR_STATE_SUBSCRIBERS = [];
const CURRENCY_SUBSCRIBERS = [];
const TERM_SUBSCRIBERS = [];

function broadcast(subscribers, data) {
  for (const subscriber of subscribers) {
    subscriber(data);
  }
}

async function get(url) {
  return new Promise((resolve, reject) => {
    $.getJSON(url, resolve).fail(reject);
  });
}

async function readCache(key, url, property) {
  const cached = window.sessionStorage.getItem(key);
  if (cached) {
    const expiration = window.sessionStorage.getItem(`${ key }-expires`);
    if (expiration > JSON.stringify(new Date())) {
      return JSON.parse(cached);
    }
  }

  const data = await get(url);
  window.sessionStorage.setItem(key, JSON.stringify(data[property]));

  const expires = new Date();
  expires.setHours(expires.getHours() + 2);
  window.sessionStorage.setItem(`${ key }-expires`, JSON.stringify(expires));

  return data[property];
}

async function loadPriceBook(pricebook) {
  return await readCache(
    `${ STORAGE_KEYS.PRICEBOOK_PREFIX }${ pricebook }`,
    `/assets/pricing/${ pricebook }.json`,
    'products');
}

async function loadPackages(solution) {
  return await readCache(
    `${ STORAGE_KEYS.SOLUTIONS_PREFIX }${ solution }`,
    `/assets/pricing/solutions-${ solution }.json`,
    'packages');
}

async function loadBundles() {
  return await readCache(
    STORAGE_KEYS.BUNDLES,
    '/assets/pricing/bundles.json',
    'bundles');
}

function setCurrencyPreference(preference) {
  window.localStorage.setItem(STORAGE_KEYS.CURRENCY, preference);
  broadcast(CURRENCY_SUBSCRIBERS, preference);
}

function setTermPreference(preference) {
  window.localStorage.setItem(STORAGE_KEYS.TERM, preference);
  broadcast(TERM_SUBSCRIBERS, preference);
}

async function getCurrencyPreference() {
  let preference = window.localStorage.getItem(STORAGE_KEYS.CURRENCY);

  if (preference) {
    return preference;
  }

  try {
    const geo = await get('https://freegeoip.app/json/');
    for (const currency in CURRENCY_MAPPING) {
      if (CURRENCY_MAPPING[currency].includes(geo.country_code)) {
        preference = currency;
      }
    }
  }
  catch {
    console.warn('Unable to get location data to set default currency.');
  }

  preference = preference || 'USD';
  setCurrencyPreference(preference);
  return preference;
}

async function formatCurrency(number) {
  const currency = await getCurrencyPreference();
  const symbol = getCurrencySymbol(currency);
  const formatter = new Intl.NumberFormat('en', { minimumFractionDigits: 2 });
  return `${ symbol } ${ formatter.format(number) }`;
}

async function formatCurrencyRounded(number) {
  const currency = await getCurrencyPreference();
  const symbol = getCurrencySymbol(currency);
  const formatter = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });
  return `${ symbol }${ formatter.format(number) }`;
}

function getTermPreference() {
  let preference = window.localStorage.getItem(STORAGE_KEYS.TERM);
  if (!preference) {
    preference = 36;
    setTermPreference(preference);
  }
  return preference * 1;
}

function registerCurrencyChangeSubscriber(handler) {
  CURRENCY_SUBSCRIBERS.push(handler);
}

function registerTermChangeSubscriber(handler) {
  TERM_SUBSCRIBERS.push(handler);
}

function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency];
}

async function getBundle(productLine, tier) {
  const bundles = await loadBundles();
  return find(bundles, { productLine, tier });
}

async function getBundleStartingPrice(productLine, tier) {
  let recurringPrice = 0;
  let oneTimePrice = 0;
  const term = getTermPreference();
  const bundle = await getBundle(productLine, tier);

  for (const product of bundle.skus) {
    if (term > product.maxTerm) {
      continue;
    }
    const priceData = await getPrice(productLine, tier, product.sku, product.defaultQuantity);
    recurringPrice += priceData.recurring;
    oneTimePrice += priceData.oneTime;
  }

  return { recurringPrice, oneTimePrice };
}

function getEstimatorState() {
  const state = window.localStorage.getItem(STORAGE_KEYS.ESTIMATOR_STATE);

  if (!state) {
    resetEstimatorState();
    return getEstimatorState();
  }

  return JSON.parse(state);
}

async function exportEstimatorState() {
  const state = getEstimatorState();

  state.currency = await getCurrencyPreference();
  state.term = getTermPreference();




  const a = document.createElement('a');
  a.href = `data:application/json;charset=utf-8,${ encodeURIComponent(JSON.stringify(state)) }`;
  a.download = `${ location.host.split('.')[1] || 'security' }_solution_estimate_${ Date.now() }.json`;
  a.style = 'visibility: hidden';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function importEstimatorState() {
  const input = document.createElement('input');
  input.type = 'file';
  input.style = 'visibility: hidden';
  input.addEventListener('change', (changeEvent) => {
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const state = JSON.parse(loadEvent.target.result);

        if (state.term) {
          setTermPreference(state.term);
        }

        if (state.currency) {
          setCurrencyPreference(state.currency);
        }

        state.term = undefined;
        state.currency = undefined;

        pushEstimatorState(state);
      }
      catch {
        // TODO: Error handling
      }
      finally {
        document.body.removeChild(input);
      }
    };
    reader.readAsText(changeEvent.target.files[0]);
  });
  document.body.appendChild(input);
  input.click();
}

function resetEstimatorState() {
  pushEstimatorState({
    name: 'Your Estimate',
    bundles: [],
  });
}

function pushEstimatorState(state) {
  window.localStorage.setItem(STORAGE_KEYS.ESTIMATOR_STATE, JSON.stringify(state));
  broadcast(ESTIMATOR_STATE_SUBSCRIBERS, state);
}

function registerStateSubscriber(handler) {
  ESTIMATOR_STATE_SUBSCRIBERS.push(handler);
}

async function addBundle(productLine, tier) {
  const bundle = await getBundle(productLine, tier);
  const state = getEstimatorState();
  const uuid = uuid4();

  state.bundles.push({
    uuid,
    productLine,
    tier,
    name: `${ bundle.productLineName } - ${ bundle.tierName }`,
    skus: map(bundle.skus, (s) => {
      return {
        sku: s.sku,
        quantity: s.defaultQuantity,
        optionGroup: s.optionGroup,
      };
    })
  });

  pushEstimatorState(state);
  return uuid;
}

function removeBundle(uuid) {
  const state = getEstimatorState();
  remove(state.bundles, { uuid });
  pushEstimatorState(state);
}

function updateQuantity(uuid, sku, quantity) {
  const state = getEstimatorState();

  for (const bundle of state.bundles) {
    if (bundle.uuid === uuid) {
      const targetSku = find(bundle.skus, { sku });

      for (const s of bundle.skus) {
        if (s.sku === sku) {
          s.quantity = quantity;
        }
        else {
          if (targetSku.optionGroup && targetSku.optionGroup === s.optionGroup) {
            s.quantity = 0;
          }
        }
      }

      break;
    }
  }

  pushEstimatorState(state);
}

async function getSkuDetail(pricebook, sku) {
  const products = await loadPriceBook(pricebook);
  return find(products, { sku });
}

async function getDiscountRate(productLine, tier) {
  const bundle = await getBundle(productLine, tier);

  if (!bundle) {
    console.log(productLine, tier);
  }

  const term = getTermPreference();
  const discount = find(DEFAULT_DISCOUNT_SCHEDULE, { term });

  if (!discount) {
    return 0;
  }

  return discount.percentage;
}

async function getMaxDiscount(productLine, tier) {
  const bundle = await getBundle(productLine, tier);
  let maxDiscount = null;
  let maxDiscountTerm = null;
  for (const discount of DEFAULT_DISCOUNT_SCHEDULE) {
    if (discount.percentage > maxDiscount) {
      maxDiscount = discount.percentage;
      maxDiscountTerm = discount.term;
    }
  }
  return {
    term: maxDiscountTerm,
    percentage: maxDiscount
  };
}

async function getPrice(productLine, tier, sku, quantity) {
  const products = await loadPriceBook(productLine);
  const product = find(products, { sku });
  const currency = await getCurrencyPreference();
  const basePrice = find(product.prices, { currency }).price;
  const discount = await getDiscountRate(productLine, tier);
  const price = (basePrice * quantity * (1 - discount)).toFixed(2) * 1;
  const undiscountedPrice = (basePrice * quantity).toFixed(2) * 1;
  const recurring = product.type === 'recurring';

  return {
    oneTime: recurring ? 0 : undiscountedPrice,
    recurring: recurring ? price : 0
  };
}

async function isSkuActive(productLine, tier, sku) {
  const term = getTermPreference();
  const bundle = await getBundle(productLine, tier);
  const product = find(bundle.skus, { sku });

  if (!product.maxTerm) {
    return true;
  }

  if (product.maxTerm >= term) {
    return true;
  }

  return false;
}

async function getQuantityParams(productLine, tier, sku) {
  const bundle = await getBundle(productLine, tier);
  const product = find(bundle.skus, { sku });
  return {
    min: product.minQuantity,
    max: product.maxQuantity,
  };
}

async function getBundleOptionGroups(productLine, tier) {
  const bundle = await getBundle(productLine, tier);
  return bundle.optionGroups;
}

async function getBundleOptionGroupValues(productLine, tier, optionGroup) {
  const bundle = await getBundle(productLine, tier);
  const options = filter(bundle.skus, { optionGroup });
  return map(options, (option) => {
    return {
      sku: option.sku,
      value: option.optionGroupValue
    };
  });
}

function unbindComponent(selector) {
  ReactDOM.unmountComponentAtNode(document.querySelector(selector));
}

function bindComponent(component, selector, args) {
  ReactDOM.render(React.createElement(component, args), document.querySelector(selector));
}

function bindComponentByName(name, selector, args) {
  bindComponent(COMPONENTS[name], selector, args);
}

window.qs = window.qs || {};
window.qs.pricing = {
  loadPriceBook,
  loadPackages,
  loadBundles,
  getCurrencyPreference,
  setCurrencyPreference,
  onCurrencyPreferenceChanged: registerCurrencyChangeSubscriber,
  formatCurrency,
  formatCurrencyRounded,
  getTermPreference,
  setTermPreference,
  onTermPreferenceChanged: registerTermChangeSubscriber,
  getCurrencySymbol,
  getBundle,
  getBundleStartingPrice,
  getBundleOptionGroups,
  getBundleOptionGroupValues,
  getSkuDetail,
  getDiscountRate,
  getMaxDiscount,
  estimator: {
    onStateChanged: registerStateSubscriber,
    getState: getEstimatorState,
    exportState: exportEstimatorState,
    importState: importEstimatorState,
    pushState: pushEstimatorState,
    resetState: resetEstimatorState,
    addBundle,
    removeBundle,
    updateQuantity,
    getPrice,
    isSkuActive,
    getQuantityParams,
  },
};

window.qs.components = {
  bindComponent,
  bindComponentByName,
  unbindComponent,
};
